/**
 * A row of icon + text controls that drops to icons only when the labels
 * would not fit — measured, not guessed from a media query.
 *
 * Layout: [start spacer] [centre group, centred] [end group]. The spacer is
 * kept as wide as the end group so the centre group is centred on the row
 * itself. When that is too wide the spacer goes first (the centre group sits
 * a little off centre but keeps its labels); then the labels go
 * (`.is-collapsed`); and if even the icons do not fit the row scrolls
 * (`.is-overflowing`).
 *
 * How it measures: expand everything, read the groups' natural widths (the
 * centre group is `width: max-content`, so its offsetWidth is its content's
 * width however the row is laid out), decide, set the classes. All of it
 * synchronously in a layout effect or a ResizeObserver callback — both run
 * before paint — so there is no flash and no extra render. The decision is
 * always made from the EXPANDED width, so it cannot oscillate. Only the row
 * itself is observed for size (class changes here alter its children, never
 * the row, so the observer is not re-fired by its own work); children that
 * change their text (a look's name, "Stage" → "Stage again") are caught by a
 * MutationObserver and by the layout effect on every render.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import './designerHeader.css';

const GAP = 8;
const GAP_COLLAPSED = 4;

export interface IconTextRowProps {
  children?: React.ReactNode;
  /** Controls at the far right (Exit, close), outside the centred group. */
  end?: React.ReactNode;
  className?: string;
}

export const IconTextRow: React.FC<IconTextRowProps> = ({ children, end, className }) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const groupRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const measure = useCallback(() => {
    const row = rowRef.current;
    const group = groupRef.current;
    if (!row || !group) return;
    const endEl = endRef.current;
    const style = window.getComputedStyle(row);
    const inner = row.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);

    row.classList.remove('is-collapsed', 'is-overflowing');
    const decide = (gap: number): { balance: number; fits: boolean } => {
      const g = group.offsetWidth;
      const e = endEl ? endEl.offsetWidth : 0;
      if (g + 2 * (e + gap) <= inner) return { balance: e, fits: true }; // centred, with the spacer
      if (g + (e ? e + gap : 0) <= inner) return { balance: 0, fits: true }; // no spacer: off centre a little
      return { balance: 0, fits: false };
    };
    let result = decide(GAP);
    if (!result.fits) {
      row.classList.add('is-collapsed');
      result = decide(GAP_COLLAPSED);
      if (!result.fits) row.classList.add('is-overflowing');
    }
    row.style.setProperty('--dh-balance', `${result.balance}px`);
  }, []);

  // Every render (children may have changed), before paint.
  useLayoutEffect(measure);

  // The row's own width (the window, a side panel), and the children's content.
  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => { frame = 0; measure(); });
    };
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => measure()) : null;
    ro?.observe(row);
    const mo = typeof MutationObserver !== 'undefined' ? new MutationObserver(schedule) : null;
    // The groups only: the row's own class and style are what measure() sets.
    if (groupRef.current) mo?.observe(groupRef.current, { childList: true, subtree: true, characterData: true });
    if (endRef.current) mo?.observe(endRef.current, { childList: true, subtree: true, characterData: true });
    // Web fonts arriving change every label's width.
    const fonts = (document as Document & { fonts?: { ready: Promise<unknown> } }).fonts;
    fonts?.ready.then(schedule, () => undefined);
    return () => {
      ro?.disconnect();
      mo?.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [measure]);

  return (
    <div ref={rowRef} className={`dh-row${className ? ` ${className}` : ''}`}>
      <div className="dh-row-start" aria-hidden="true" />
      <div className="dh-row-center">
        <div ref={groupRef} className="dh-group">{children}</div>
      </div>
      {end && <div ref={endRef} className="dh-row-end">{end}</div>}
    </div>
  );
};

export default IconTextRow;
